import React from 'react';
import { CompactError, RobbieOverlay, StepBar } from '../../ui';
import UploadDropZone, { UploadDropZoneProps } from './UploadDropZone';
import UploadFileRow, { UploadRow } from './UploadFileRow';
import BomStarterFiles from './BomStarterFiles';
import RobbieWillStrip from './RobbieWillStrip';
import UploadCreditsNote, { ExtractionSummary } from './UploadCreditsNote';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import { BomPricing } from '../../../hooks/useBomPricing';
import { UPLOAD_LIMIT_LABEL } from '../../../utils/uploadLimits';
import { extractionCopy } from '../../../utils/waitCopy';

/**
 * Wizard Step 1 — the self-contained Upload panel ported from
 * `design/RFQ Wizard v2.dc.html`: "Your files · N" card (drop zone +
 * Robbie's starter-file downloads + per-file rows), "Robbie will:" strip,
 * and the StepBar. While Continue reads the files a Robbie overlay covers the
 * panel (extraction is one synchronous call, so there is no "you can leave"
 * line); a failed read shows in the same card. Purely
 * presentational so it renders inside today's wizard page and re-mounts
 * unchanged once the wizard page adopts the v2 chrome. Logic lives in
 * useUploadStep.
 *
 * A viewer on someone else's RFQ sees the file list and nothing that adds to
 * it: no drop zone, no starter templates, no remove, no promise about what
 * Robbie will do with a file they cannot supply.
 */

export interface UploadStepPanelProps {
  rows: UploadRow[];
  dropzone?: UploadDropZoneProps;
  onRemove?: (index: number) => void;
  onBack?: () => void;
  onContinue?: () => void;
  isExtracting?: boolean;
  continueDisabled?: boolean;
  extractionError?: string | null;
  /** What discovery will cost; omitted in read-only and gallery renders. */
  pricing?: BomPricing;
  /** What Continue extracted this session. */
  extraction?: ExtractionSummary | null;
  /** The uploaded files Continue reads — the overlay and the StepBar name them. */
  readyNames?: string[];
  /** Past the usual range: the overlay says so instead of the range. */
  longWait?: boolean;
  /** The extraction call failed; the overlay shows the error in place. */
  extractionFailed?: boolean;
  onDismissFailure?: () => void;
}

export const UploadStepPanel: React.FC<UploadStepPanelProps> = ({
  rows,
  dropzone,
  onRemove,
  onBack,
  onContinue,
  isExtracting = false,
  continueDisabled = false,
  extractionError,
  pricing,
  extraction,
  readyNames = [],
  longWait = false,
  extractionFailed = false,
  onDismissFailure,
}) => {
  const readOnly = useReadOnly();
  const names = readyNames.length > 0 ? readyNames : rows.map((r) => r.name);
  const summary =
    names.length === 0 ? 'No files yet' : `${names.length} ${names.length === 1 ? 'file' : 'files'} · ${names.join(', ')}`;

  return (
  <div className="relative flex flex-col gap-[16px] font-sans">
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <div className="flex flex-wrap items-baseline justify-between gap-x-s5 border-b border-line-divider-soft px-[18px] py-[12px]">
        <span className="text-[14px] font-bold text-ink">
          {readOnly ? 'Files' : 'Your files'} · {rows.length}
        </span>
        {!readOnly && (
          <span className="text-body-sm text-ink-muted">BOM, datasheets, drawings — {UPLOAD_LIMIT_LABEL}</span>
        )}
      </div>
      {!readOnly && (
        <>
          <BomStarterFiles />
          <div className={`px-[18px] py-[14px] ${rows.length > 0 ? 'border-b border-line-divider' : ''}`}>
            <UploadDropZone {...dropzone} />
          </div>
        </>
      )}
      {readOnly && rows.length === 0 && (
        <p className="m-0 px-[18px] py-[14px] text-body-md italic text-ink-muted">
          No files on this RFQ yet.
        </p>
      )}
      {rows.map((row, index) => (
        <UploadFileRow
          key={`${row.name}-${index}`}
          row={row}
          onRemove={!readOnly && onRemove ? () => onRemove(index) : undefined}
        />
      ))}
      {/* Extraction is free; discovery is what is charged. The rule before
          Continue, what was extracted after it. A viewer cannot upload, so
          they see neither. */}
      {!readOnly && pricing && (
        <div className="border-t border-line-divider-soft px-[18px] py-[12px]">
          <UploadCreditsNote pricing={pricing} extraction={extraction} />
        </div>
      )}
    </div>

    {!readOnly && <RobbieWillStrip />}

    {extractionError && !extractionFailed && <CompactError layout="row" title={extractionError} />}

    <StepBar
      stepLabel="Step 1 · Upload"
      summary={summary}
      onBack={onBack}
      cta={{ label: 'Continue to sourcing criteria', onClick: onContinue, disabled: continueDisabled }}
    />

    <RobbieOverlay
      open={isExtracting || extractionFailed}
      title={extractionCopy.title(names.length)}
      sub={names.join(' · ')}
      usual={extractionCopy.usual(Math.max(names.length, 1))}
      long={longWait}
      error={
        extractionFailed ? (
          <CompactError
            title={extractionCopy.failedTitle}
            message={extractionCopy.failedMessage}
            action={onContinue ? { label: 'Try again', onClick: onContinue } : undefined}
            secondaryAction={onDismissFailure ? { label: 'Back to my files', onClick: onDismissFailure } : undefined}
          />
        ) : undefined
      }
    />
  </div>
  );
};

export default UploadStepPanel;
